import { useState } from "react";
import { Button, CircularProgress, Tooltip } from "@mui/material";
import { useDispatch, useSelector } from "react-redux";
import { canManuallyRecalculateCredit } from "@vmosolutions/oc-interfaces";
import type { ICalculatePlannedCreditResult, ICreditClientSetting } from "@vmosolutions/oc-interfaces";
import { openSnackbar } from "src/redux/actions/App.Actions";
import type { IRootState } from "src/redux/reducers/CombinedReducers";
import type { ISnackbarState } from "src/redux/reducers/SnackbarReducer";
import { recalculatePairingCredit } from "./RecalculateCredit";

/** Groupings named as the operator sees them on the credit fields, not by their raw field keys. */
const displayNames = (fields: string[], settings: ICreditClientSetting[]): string =>
  fields.map((f) => settings.find((s) => s.plannedCreditField === f)?.displayName ?? f).join(", ");

/**
 * The outcome read off the result rather than off the call having returned: a run can partly succeed, and
 * "Credit recalculated" over a grouping whose module timed out would misreport a payroll number.
 */
const outcomeSnackbar = (result: ICalculatePlannedCreditResult, settings: ICreditClientSetting[]): ISnackbarState => {
  const writtenFields = Object.keys(result?.written ?? {});
  const failedFields = result?.failed ?? [];
  // Every grouping that was called gets a record written, failures included - a failed one keeps its last
  // known good values under an error status. So the successes are what is left after the failures.
  const ok = displayNames(
    writtenFields.filter((field) => !failedFields.includes(field)),
    settings
  );
  const failed = displayNames(failedFields, settings);
  if (failed) {
    // No closeDelay - a failure stays up until dismissed.
    return {
      open: true,
      closeDelay: null,
      variant: ok ? "warning" : "error",
      message: ok
        ? `Credit recalculated for ${ok}. ${failed} failed and kept ${
            failedFields.length > 1 ? "their previous values" : "its previous value"
          }.`
        : `Credit recalculation failed for ${failed}. The previous values are unchanged.`,
    };
  }
  if (!ok) {
    // Nothing written and nothing failed: no payroll module matched this pairing.
    return {
      open: true,
      closeDelay: 3000,
      variant: "info",
      message: "No credit was calculated - no payroll module applies to this pairing.",
    };
  }
  return { open: true, closeDelay: 3000, variant: "success", message: `Credit recalculated for ${ok}` };
};

type IRecalculateCreditButtonProps = {
  /** The pairing to recalculate. A "new..." placeholder id means nothing is saved yet. */
  pairingId: string;
  /** True when the editor holds unsaved edits - the backend would calculate from the saved content. */
  pairingDirty: boolean;
  /** Called with the run's result. The parent owns both copies of the pairing, so it applies the records. */
  onRecalculated: (result: ICalculatePlannedCreditResult) => void | Promise<void>;
};

/**
 * The admin Recalculate action for a pairing's calculated credit.
 * Hidden entirely unless the customer's credit settings can calculate
 */
export const RecalculateCreditButton = (props: IRecalculateCreditButtonProps): JSX.Element | null => {
  const { pairingId, pairingDirty, onRecalculated } = props;
  const dispatch = useDispatch();
  const creditSettingValue = useSelector((state: IRootState) => state.credit);
  const [recalculating, setRecalculating] = useState<boolean>(false);
  if (!canManuallyRecalculateCredit(creditSettingValue)) {
    return null;
  }

  // A pairing that has never been saved has no stored content to calculate from.
  const isNew = pairingId?.includes("new") ?? true;
  const blockedReason = isNew
    ? "Create the pairing before recalculating its credit"
    : pairingDirty
      ? "Save your changes first - credit is calculated from the saved pairing"
      : undefined;

  const handleClick = async (): Promise<void> => {
    setRecalculating(true);
    try {
      const result = await recalculatePairingCredit(pairingId, dispatch);
      // A failed call is already reported by the snackbar
      if (!result) {
        return;
      }
      dispatch(openSnackbar(outcomeSnackbar(result, creditSettingValue.settings ?? [])));
      await onRecalculated(result);
    } finally {
      setRecalculating(false);
    }
  };

  return (
    <Tooltip title={blockedReason ?? "Clicking Recalculate updates the planned credit only"}>
      {/* Span so the tooltip still fires while the button is disabled. */}
      <span>
        <Button
          id="recalculate_credit_btn"
          size="small"
          variant="outlined"
          disabled={recalculating || blockedReason !== undefined}
          startIcon={recalculating ? <CircularProgress size={12} thickness={5} color="inherit" /> : undefined}
          onClick={() => void handleClick()}
          sx={{ marginLeft: "8px", textTransform: "none", fontSize: 11, padding: "1px 8px" }}
        >
          {recalculating ? "Recalculating..." : "Recalculate"}
        </Button>
      </span>
    </Tooltip>
  );
};
